import { useCallback, useEffect, useRef, useState } from "react";

import { getAppRedirectEnv } from "@/utils/auth0/appRedirect/env";

import { resolveAppRedirectUrl } from "./resolveAppRedirectUrl";

interface UseAppRedirectResult {
  error: boolean;
  redirectUrl: string | null;
}

interface InFlightResolve {
  apiBaseUrl: string;
  clientId: string;
  locale: string;
  promise: Promise<string>;
}

/**
 * Prefetches the client + locale application URL.
 * `useSuccessAutoRedirect` controls when to navigate to the resolved URL.
 * A repeat render for the same client, locale, and API reuses the in-flight request.
 */
export function useAppRedirect(
  clientId: string | null | undefined,
  locale: string,
): UseAppRedirectResult {
  const [error, setError] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const inFlightRef = useRef<InFlightResolve | null>(null);

  const resolveUrl = useCallback(
    async (apiBaseUrl: string, clientId: string): Promise<string> => {
      // In development, React StrictMode can run the effect twice on mount.
      // Share the pending request so this sends only one POST to the resolver.
      const inFlight = inFlightRef.current;
      if (
        inFlight?.apiBaseUrl === apiBaseUrl &&
        inFlight.clientId === clientId &&
        inFlight.locale === locale
      ) {
        return inFlight.promise;
      }

      const request = resolveAppRedirectUrl({
        apiBaseUrl,
        clientId,
        locale,
      }).finally(() => {
        // A customer in Switzerland may switch from German to French mid-request.
        // Only clear this request's entry, preserving any newer language request.
        if (inFlightRef.current?.promise === request) {
          inFlightRef.current = null;
        }
      });

      inFlightRef.current = {
        apiBaseUrl,
        clientId,
        locale,
        promise: request,
      };
      return request;
    },
    [locale],
  );

  useEffect(() => {
    let ignore = false;
    const { apiBaseUrl } = getAppRedirectEnv();

    if (!clientId || apiBaseUrl === undefined) {
      setError(true);
      setRedirectUrl(null);
      return;
    }

    setError(false);
    // After a language switch, the previous destination may already be ready.
    // Clear it so the timer cannot send the customer there while we resolve again.
    setRedirectUrl(null);

    void resolveUrl(apiBaseUrl, clientId)
      .then((url) => {
        if (ignore) {
          return;
        }
        setRedirectUrl(url);
      })
      .catch(() => {
        if (ignore) {
          return;
        }
        setError(true);
      });

    return () => {
      // If the customer switches language or leaves this screen, ignore this
      // request's late response: neither success nor failure should affect the UI.
      ignore = true;
    };
  }, [clientId, locale, resolveUrl]);

  return {
    error,
    redirectUrl,
  };
}
